import { motion } from 'motion/react';
import Link from 'next/link';
import { memo } from 'react';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { PopularSearchCarousel } from '@/features/GlobalSearch/components/shared/PopularSearchCarousel';
import { SectionHeading } from '@/features/GlobalSearch/components/trending/SectionHeading';
import { getTrendingResultUrl } from '@/features/GlobalSearch/utils/getTrendingSearches';
import useTWScreens from '@/hooks/ui/useTWScreens';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponseHit } from '@music-vine/setlist';
interface SfxTrendingSectionProps {
  closePopup: () => void;
  onNameClick: (content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => void;
  trackTrendingClick: (endSlug: string) => void;
  trendingSfxCollections?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
  trendingSfxSearches?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
}
export const SfxTrendingSection = memo(({
  trendingSfxSearches,
  trendingSfxCollections,
  closePopup,
  trackTrendingClick,
  onNameClick
}: SfxTrendingSectionProps) => {
  const {
    isMd,
    isXl
  } = useTWScreens();
  const hasCollections = trendingSfxCollections?.hits && trendingSfxCollections.hits.length > 0;

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedContainer = isMd ? motion.div : 'div';
  const AnimatedItem = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} initial={isMd ? {
    opacity: 0
  } : undefined} style={{
    position: 'relative'
  }} transition={isMd ? {
    duration: 0.25
  } : undefined}>
        <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
          <div className="mb-2">
            <SectionHeading>Popular searches</SectionHeading>
          </div>

          {/* Desktop view */}
          <div className="hidden max-h-6 overflow-hidden md:flex md:flex-wrap md:gap-2 focus-within:[&:has(:focus-visible)]:max-h-none focus-within:[&:has(:focus-visible)]:overflow-visible">
            {trendingSfxSearches?.hits?.slice(0, 10).map(hit => {
          const {
            document
          } = hit;
          const href = getTrendingResultUrl({
            name: document.name ?? '',
            type: 'sfx'
          });
          return <AnimatedItem animate={isMd ? {
            opacity: 1,
            scale: 1
          } : undefined} initial={isMd ? {
            opacity: 0,
            scale: 0.95
          } : undefined} key={document.id} transition={isMd ? {
            duration: 0.2
          } : undefined}>
                  <SearchButton assetType="sfx" closePopup={closePopup} href={href} id={document.id} name={document.name ?? ''} onNameClick={onNameClick} testId="popular-sfx-search" />
                </AnimatedItem>;
        })}
          </div>

          {/* Mobile Carousel */}
          <div className="-mx-4 block md:hidden">
            <PopularSearchCarousel assetType="sfx" closePopup={closePopup} hits={trendingSfxSearches?.hits} onNameClick={onNameClick} testId="popular-sfx-search" />
          </div>
        </div>

        {hasCollections && trendingSfxCollections?.hits && <div className="main-search-results my-4 px-4 md:my-0 md:mb-0 md:px-0">
            <div className="mb-2">
              <SectionHeading>Collections</SectionHeading>
            </div>
            <motion.div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {trendingSfxCollections.hits.slice(0, 6).map((hit, idx) => {
          const {
            document
          } = hit;
          const href = document.slug;
          return <AnimatedItem animate={isMd ? {
            opacity: 1
          } : undefined} initial={isMd ? {
            opacity: 0
          } : undefined} key={`collection-${document.id}`} transition={isMd ? {
            duration: 0.15,
            delay: Math.min(idx * 0.02, 0.1)
          } : undefined}>
                    <div>
                      <Link className="relative block aspect-square cursor-pointer overflow-hidden rounded-lg bg-gray-50 transition-all duration-200 hover:scale-[1.02] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-pink-500 dark:bg-gray-900" href={href} onClick={() => {
                closePopup();
                trackTrendingClick(href);
              }}>
                        <motion.div className="absolute inset-0 bg-center bg-cover" style={{
                  backgroundImage: `url(${document.image})`
                }} />
                      </Link>
                      <div className="mt-2">
                        <h3 className="line-clamp-2 font-sans font-semibold text-gray-950 text-sm dark:text-white">
                          {document.name}
                        </h3>
                        {document.short_description && <p className="mt-1 line-clamp-1 text-gray-700 text-xs dark:text-gray-200">
                            {document.short_description}
                          </p>}
                      </div>
                    </div>
                  </AnimatedItem>;
        })}
            </motion.div>
          </div>}
      </AnimatedContainer>;
});
SfxTrendingSection.displayName = 'SfxTrendingSection';